/* eslint-disable @typescript-eslint/member-ordering */
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Pipe,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { omit } from "lodash";
import { User } from "src/app/modules/portal/users/user.type";
import { UserInfo } from "../../types/user-info";
import { QuestionBase } from "./question-base";
import { FormOptions, QuestionControlService } from "./question-control.service";
import { DropdownQuestion } from "./question-dropdown";
import { ImageQuestion } from "./question-image";

export interface DynamicFormCustomBtn {
  label: string;
  onClick: () => void;
  disabled?: boolean | (() => boolean);
  warnMessage?: string;
}

export interface DynamicFormEditBtn {
  label?: string;
  onClick?: () => void;
  show?: boolean;
  hiddenPipe?: {
    name: Pipe;
    args?: {
      currentUser: UserInfo;
      userToEdit: User;
    };
  };
}
@Component({
  selector: "app-dynamic-form",
  templateUrl: "./dynamic-form.component.html",
  providers: [QuestionControlService],
})
export class DynamicFormComponent implements OnInit, OnDestroy, AfterContentChecked {
  @Input() showButtons = true;
  @Input() saveDisabled = false;
  @Input() customBtn: DynamicFormCustomBtn;
  @Input() editBtn: DynamicFormEditBtn = {
    show: false,
    label: "Edit",
  };
  @Input() bindEnabled = false;
  @Input() saveLabel = "Save";
  @Input() questions: QuestionBase[] = [];
  @Output() formSubmit = new EventEmitter<any>();
  @Output() hasChanged = new EventEmitter<any>();
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(disable: boolean) {
    this._disabled = disable;
    this.toggleFormStatus();
  }
  @Input() button: HTMLButtonElement;
  form: FormGroup;
  private _disabled = false;
  private cachedFormValue = {};

  constructor(private qcs: QuestionControlService, private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.validateForm(this.questions, { disabled: this._disabled });
    this.cachedFormValue = this.form.getRawValue();
  }

  ngOnDestroy(): void {
    this.formSubmit.unsubscribe();
    this.hasChanged.unsubscribe();
  }

  resetWithValues(questions: QuestionBase[]): void {
    this.validateForm(questions, { disabled: this._disabled });

    this.cachedFormValue = { ...this.cachedFormValue, ...this.form.getRawValue() };
  }

  validateForm(questions: QuestionBase[], options?: FormOptions): void {
    this.form = this.qcs.toFormGroup(questions, {
      previousForm: this.form,
      ...options,
    });
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  getQuestion(key: string): QuestionBase {
    return this.questions.find((q) => q.key === key);
  }

  removeQuestion(key: string): void {
    this.questions = this.questions.filter((q) => q.key !== key);
    this.validateForm(this.questions);
  }

  showFormButtons(value: boolean): void {
    this.showButtons = value;
  }

  replaceOrAppendQuestion(question: QuestionBase): void {
    const questionToReplace = this.getQuestion(question.key);
    const questionOrders = this.questions.map((qs) => qs.order);
    const questionIndex = questionOrders.findIndex((order) => order === question.order);
    this.questions.splice(questionIndex, questionToReplace ? 1 : 0, question);
    // sort here is required to assure the order of the questions/dropdowns
    this.questions.sort((a, b) => a.order - b.order);
    this.validateForm(this.questions);
  }

  onChange(event: any): void {
    this.hasChanged.emit(event);
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.cachedFormValue = { ...this.cachedFormValue, ...this.form.getRawValue() };
      const ignoredQuestionKeys = this.questions.filter((qs) => qs.ignoreInSubmit).map((qs) => qs.key);
      const rawValues = this.form.getRawValue();
      const values = omit(rawValues, ignoredQuestionKeys);
      this.disable();
      this.formSubmit.emit(values);
    } else {
      this.form.markAllAsTouched();
    }
  }

  toggleFormStatus(): void {
    if (!this.form) {
      return;
    }

    // enable form
    if (!this.disabled && this.form.disabled) {
      this.form.enable({ emitEvent: false });

      this.questions.forEach((q) => {
        this.form.controls[q.key]?.setErrors(null);
        const isDropdownEmpty = q instanceof DropdownQuestion && q.options?.length === 0;
        if (q.disabled || isDropdownEmpty) {
          this.form.controls[q.key]?.disable({ emitEvent: false });
        }
      });
    }

    // disable form
    if (this.disabled && this.form.enabled) {
      this.form.reset(this.cachedFormValue);
      this.form.disable({ emitEvent: false });
      this.questions.forEach((q) => {
        this.form.controls[q.key]?.disable({ emitEvent: false });
        // reset selected file/image on each ImageQuestion
        if (q instanceof ImageQuestion && q.imageReqs.inputNode) {
          q.reset();
        }
      });
    }
  }

  markQuestionAsPristine(key: string): void {
    this.form.controls[key].markAsPristine();
  }

  resetForm(): void {
    this.form.reset();
  }

  enable(): void {
    this.saveDisabled = false;
  }

  disable(): void {
    this.saveDisabled = true;
  }

  isDisabled(): boolean {
    const controls = this.form.controls;

    const hasInvalidDisabledFields = Object.keys(controls).some(
      (key) =>
        controls[key].disabled && this.questions.find((qs) => qs.key === key).required && controls[key]?.value === "",
    );
    return this.saveDisabled || this.form.invalid || this.form.pristine || hasInvalidDisabledFields;
  }
}
